
/**
 * Module dependencies.
 */

import { ProductApplication } from 'src/types/products';
import { getProductApplicationImage } from 'src/core/utils/images';
import { units } from 'src/styles/utils';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import Loading from 'src/components/core/loading';
import React, { ReactElement, useMemo } from 'react';
import Type from 'src/components/core/typography/index';
import filter from 'lodash/filter';
import get from 'lodash/get';
import head from 'lodash/head';
import keys from 'lodash/keys';
import map from 'lodash/map';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import split from 'lodash/split';
import styled from 'styled-components';
import useProductApplications from 'src/api/app/products/use-product-applications';

/**
 * `Props` type.
 */

type Props = {
  filterName: string
}

/**
 * `LoadingWrapper` styled component.
 */

const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 150px;
`;

/**
 * `ApplicationFilter` component.
 */

const ApplicationFilter = ({ filterName }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { data, isLoading, isSuccess } = useProductApplications();
  const hasApplications = size(data) > 0;
  const { filters } = useFiltersList();
  const selectedFilters = useMemo(() => {
    const hasFilter = keys(filters).includes(filterName);

    return hasFilter ? split(filters[filterName], ',').map((id: string) => {
      return get(head(filter(data, { id })), 'name');
    }) : [];
  }, [data, filterName, filters]);

  const applications = useMemo(() => sortBy(data, ['name', 'asc']), [data]);

  return (
    <FilterDisclosure
      name={filterName}
      selectedFilters={selectedFilters}
      title={translate('common:labels.application')}
    >
      {isLoading && (
        <LoadingWrapper>
          <Loading active />
        </LoadingWrapper>
      )}

      {isSuccess && !hasApplications && (
        <Type.Paragraph paddingVertical={units(1)}>
          {translate('collections:list.filters.emptyAplications')}
        </Type.Paragraph>
      )}

      {isSuccess && hasApplications && map(applications, ({ externalIdentifier, id, name }: ProductApplication) => (
        <FilterItem
          key={id}
          label={(
            <FilterItemLabel
              title={name}
              type={'image'}
              value={getProductApplicationImage(externalIdentifier)}
            />
          )}
          name={filterName}
          value={id}
        />
      ))}
    </FilterDisclosure>
  );
};

/**
 * Export `ApplicationFilter` component.
 */

export default ApplicationFilter;
